import React, { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Slide from "@mui/material/Slide";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";

import { KMKDialog } from "../Common";

import { setPrevState } from "../../util/common";
import { DSColumns, PropertiesForDS } from "./DataCatalogData";
import KMKTable from "../Common/Table/KMKTable";

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: "24px 24px 24px 24px",
    backgroundColor: "#FFFFFF",
  },
  bodyContainer: {
    width: "100%",
    height: "100%",
    marginLeft: "0",
    marginBottom: "0",
    display: "flex",
    flexDirection: "column",
  },
  Toolbar: {
    backgroundColor: "white",
    color: "black",
    padding: 0,
  },
  PanelContainer: {
    padding: "0",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
    "& .MuiTableContainer-root": {
      borderRadius: "4px",
      "&::-webkit-scrollbar-track": {
        marginTop: "36px",
      },
    },
    "&.MuiGrid-item": {
      paddingTop: 0,
    },
  },
  dialogContent: {
    padding: "0",
    overflow: "hidden",
  },
  PropertyHeading: {
    fontSize: "1.1rem",
    fontWeight: "bolder",
    maxWidth: "200px",
  },
  TextGrid: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
  },
}));

const searchInArrOfObjs = ({ arr, search = "", properties = [] }) => {
  return arr.filter((obj) =>
    properties.some((property) => obj[property]?.toLowerCase().includes(search.toLowerCase()))
  );
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PropertyItem = React.memo(({ name, value, icon }) => {
  const { classes } = useStyles();

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid>
        <Typography variant="subtitle1" className={classes.PropertyHeading}>
          {`${name} - `}
        </Typography>
      </Grid>
      <Grid sx={{ wordBreak: "break-all" }}>
        <Typography sx={{ display: "flex" }}>
          {icon || <></>} {value}
        </Typography>
      </Grid>
    </Grid>
  );
});

const DSProperties = React.memo(({ selectedDC }) => {
  const { classes } = useStyles();

  return PropertiesForDS.map(({ name, id, icon, ...props }) => (
    <Grid key={id} className={classes.TextGrid}>
      <PropertyItem name={name} value={selectedDC?.[id] || "-"} icon={icon || false} />
    </Grid>
  ));
});

function ViewProfile({ visible, selectedDC, handleClose }) {
  const { classes } = useStyles();

  const [options, setOptions] = useState({
    orderBy: "Name",
    order: "asc",
    search: "",
  });
  const previewTableRef = useRef(null);
  const filterRef = useRef(null);
  const listingRef = useRef(options);

  const data = useMemo(() => {
    const arr = selectedDC?.Fields ?? [];
    if (options?.search?.length > 0) {
      return searchInArrOfObjs({
        arr,
        search: options.search,
        properties: ["Name", "Type", "References"],
      });
    }

    return arr;
  }, [options.search, selectedDC?.Fields]);

  const setOptionsHelper = (properties) => {
    setPrevState(setOptions, properties);
  };

  const updateListingRefObj = useCallback(
    (passedOptions = {}) => {
      listingRef.current = { ...options, ...passedOptions };
    },
    [options]
  );

  const handleSorting = useCallback(
    (property) => {
      const isAsc = options.orderBy === property && options.order === "asc";
      let tmpOrder = isAsc ? "desc" : "asc";

      setOptionsHelper({ order: tmpOrder, orderBy: property });
      updateListingRefObj({ orderBy: property, order: tmpOrder });
    },
    [options.order, options.orderBy, updateListingRefObj]
  );

  const handleClearSearch = () => {
    setOptionsHelper({ search: "" });
    updateListingRefObj({
      search: "",
    });
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      updateListingRefObj();
    }
  };

  const $columns = useMemo(() => {
    return ((selectedDC.TypeId === 1 ? DSColumns : []) ?? []).map((column) => ({
      id: column.id,
      name: column.label.toUpperCase(),
      style: { minWidth: 180, maxWidth: 290, wordBreak: "break-word" },
      align: column.align,
      sortable: column.sortable,
      renderHeadCell: (name) => <>{name}</>,
      renderValueCell: (val, record, index) => record?.[column.id] || "-",
    }));
  }, [selectedDC.TypeId]);

  const $sortedData = useMemo(() => {
    const primaryKey = options.orderBy;
    const sorted = [...data].sort((a, b) => {
      const aPrimary = (a[primaryKey] || "").toLowerCase();
      const bPrimary = (b[primaryKey] || "").toLowerCase();

      const primaryResult =
        options.order === "asc" ? aPrimary.localeCompare(bPrimary) : bPrimary.localeCompare(aPrimary);

      if (primaryResult !== 0) return primaryResult;

      // Secondary sort
      const aSecondary = (a.Name || "").toLowerCase();
      const bSecondary = (b.Name || "").toLowerCase();

      return options.order === "asc" ? aSecondary.localeCompare(bSecondary) : bSecondary.localeCompare(aSecondary);
    });

    return sorted;
  }, [options.orderBy, options.order, data]);

  return (
    <>
      <KMKDialog
        TransitionComponent={Transition}
        open={visible}
        sx={{
          "& .MuiDialog-paperFullScreen": {
            maxHeight: "97%",
            width: "90%",
          },
        }}
        fullScreen
      >
        <DialogTitle sx={{ padding: "0" }}>
          <AppBar sx={{ position: "relative" }}>
            <Toolbar className={classes.Toolbar}>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                Data Catalog Profile
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClose}
                style={{ marginRight: 15 }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ width: "100%", height: "100%" }} className={classes.container}>
            <Box sx={{ mb: 2 }} className={classes.bodyContainer}>
              <Box className={classes.PanelContainer} sx={{ maxHeight: "40%" }} ref={previewTableRef}>
                <Grid container spacing={2}>
                  {selectedDC.TypeId === 1 ? <DSProperties selectedDC={selectedDC} /> : <></>}
                </Grid>
              </Box>
              <Box
                className={classes.PanelContainer}
                sx={{
                  maxHeight: `calc(100% - ${previewTableRef?.current?.clientHeight || 0}px)`,
                  flex: 1,
                  overflowY: "clip",
                  marginTop: "10px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems={"baseline"}
                  sx={{ marginBottom: "10px" }}
                  ref={filterRef}
                >
                  <TextField
                    data-id="AttributeSearch"
                    margin="normal"
                    size="small"
                    placeholder="Search"
                    value={options.search}
                    variant="standard"
                    InputProps={{
                      startAdornment: <SearchIcon color="primary" />,
                      ...(options.search && {
                        endAdornment: (
                          <IconButton onClick={handleClearSearch} size="small">
                            <ClearIcon color="primary" />
                          </IconButton>
                        ),
                      }),
                    }}
                    sx={{ margin: "10px 20px 0 0", width: "300px", minWidth: "250px" }}
                    onChange={(e) => setOptionsHelper({ search: e.target.value })}
                    onKeyPress={handleSearch}
                  />
                </Box>
                <Paper
                  elevation={3}
                  className={classes.Panel}
                  direction="column"
                  sx={{
                    overflowX: "hidden",
                    overflowY: "hidden",
                    height: `calc(100% - 16px - ${filterRef?.current?.clientHeight || 0}px)`,
                    width: "100%",
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <KMKTable
                    dataIdKey="DQM_RULE_TABLE"
                    loading={false}
                    columns={$columns}
                    data={$sortedData}
                    sortingProperties={{ order: options.order, orderBy: options.orderBy, handleSorting }}
                    paginationProperties={{}}
                    tableProps={{ size: "small", stickyHeader: true }}
                    boldHeader
                    containerHeight={500}
                    noDataText={"No records found"}
                  />
                </Paper>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </KMKDialog>
    </>
  );
}

export default ViewProfile;
